import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Loader2, AlertCircle } from "lucide-react";

const isSafari = () => {
  const ua = navigator.userAgent;
  return /^((?!chrome|android).)*safari/i.test(ua);
};

async function directAuthFetch(email: string, password: string) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);

  try {
    const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseKey,
        Authorization: `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify({ email, password }),
      signal: controller.signal,
      credentials: "omit",
    });

    clearTimeout(timeout);

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { success: false, error: body?.error_description || body?.msg || "Invalid credentials." };
    }

    const data = await res.json();

    // Manually set the session in Supabase client
    if (data.access_token && data.refresh_token) {
      await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
    }

    return { success: true };
  } catch (err: any) {
    clearTimeout(timeout);
    if (err?.name === "AbortError") {
      return { success: false, error: "Request timed out. Please check your connection and try again." };
    }
    return { success: false, error: "Network error, please check your connection or try a different browser." };
  }
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function attemptLogin(): Promise<{ success: boolean; error?: string }> {
    // For Safari or if SDK fails, use direct fetch with credentials: "omit"
    const useDirect = isSafari();

    if (useDirect) {
      // Try direct fetch first for Safari
      const directResult = await directAuthFetch(email, password);
      if (directResult.success) return { success: true };

      // If direct fetch also failed with a network error, retry once via SDK
      if (directResult.error?.toLowerCase().includes("network")) {
        const { error: sdkError } = await supabase.auth.signInWithPassword({ email, password });
        if (!sdkError) return { success: true };
      }

      return { success: false, error: directResult.error };
    }

    // Standard SDK path with retries for non-Safari
    for (let i = 0; i <= 2; i++) {
      try {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });

        if (!signInError) return { success: true };

        const msg = signInError.message?.toLowerCase() ?? "";
        const isNetworkError = msg.includes("load failed") || msg.includes("fetch") || msg.includes("network");

        if (isNetworkError && i < 2) {
          await new Promise(r => setTimeout(r, 1000 * (i + 1)));
          continue;
        }

        // On final network failure, try direct fetch as fallback
        if (isNetworkError) {
          return directAuthFetch(email, password);
        }

        return { success: false, error: "Invalid credentials. Please try again." };
      } catch {
        if (i < 2) {
          await new Promise(r => setTimeout(r, 1000 * (i + 1)));
          continue;
        }
        return directAuthFetch(email, password);
      }
    }
    return { success: false, error: "Network error, please refresh or try a different browser." };
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const result = await attemptLogin();

    if (!result.success) {
      setError(result.error ?? "Something went wrong.");
      setLoading(false);
      return;
    }

    navigate("/admin/import");
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-4">
        <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
          <Shield className="w-4 h-4 text-primary" />
          <span>Internal Admin Access</span>
        </div>

        <Card className="border-border bg-card">
          <CardHeader className="text-center">
            <CardTitle className="font-display text-xl">Sign In</CardTitle>
            <CardDescription>AdV3nture admin portal</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@adv3nture.io"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              {error && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
